import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  Button,
  IconButton,
} from '@mui/material';
import {
  People,
  Assignment,
  EventNote,
  TrendingUp,
  CalendarToday,
  Upload,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import AttendanceMarking from '../Attendance/AttendanceMarking';
import MarksUpload from '../Marks/MarksUpload';
import MyClasses from '../Teacher/MyClasses';
import AttendanceReport from '../Reports/AttendanceReport';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalClasses: 0,
    totalStudents: 0,
    todayAttendance: 0,
    pendingMarks: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await dashboardAPI.getTeacherStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card sx={{ height: '100%', bgcolor: `${color}.50` }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: `${color}.main`,
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome, {user?.full_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Teacher Dashboard - Manage your classes and students
          </Typography>
        </Box>
        <IconButton onClick={loadDashboardData} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={People}
            title="Total Students"
            value={stats.totalStudents}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={EventNote}
            title="My Classes"
            value={stats.totalClasses}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={CalendarToday}
            title="Today's Attendance"
            value={`${stats.todayAttendance}%`}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={Assignment}
            title="Pending Marks"
            value={stats.pendingMarks}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<EventNote />} label="Mark Attendance" iconPosition="start" />
            <Tab icon={<Upload />} label="Upload Marks" iconPosition="start" />
            <Tab icon={<People />} label="My Classes" iconPosition="start" />
            <Tab icon={<TrendingUp />} label="Reports" iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <AttendanceMarking />}
          {activeTab === 1 && <MarksUpload />}
          {activeTab === 2 && <MyClasses />}
          {activeTab === 3 && <AttendanceReport />}
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Grid container spacing={2} mt={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<EventNote />}
                onClick={() => setActiveTab(0)}
              >
                Mark Today's Attendance
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setActiveTab(1)}
              >
                Upload Exam Marks
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last attendance marked: Today, 10:30 AM
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last marks uploaded: Yesterday, 3:45 PM
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TeacherDashboard;
